import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, Label, Tab } from 'semantic-ui-react'
import MilkFixedService from '~/api/services/milkFixedPrice'
import Layout from '~/components/Layout'
import notify from '~/components/Notify'
import { MilkFixedPriceQuery } from '~/QueryHooks/MilkFixePriceQuery'
import { RouteTypes } from '~/routes/constants'
import { TableFooterWrapper } from '../style'
import ProfileSettingsService from '~/api/services/profileSettings'
import { useAuth } from '~/routes/components/AuthProvider'
import TableComponent from '~/components/TableComponent'
import Dialog from '~/components/Dialog'
import dayjs from 'dayjs'
import { LIGHT_USER_ROLE_SLUG, OPERATIVE_ROLE_SLUG } from '~/Constants'
import CustomInput from '~/components/CustomInput'
import {
  dynamicConfirmOffersColumns,
  dynamicOffersPreviewColumns,
  dynamicParticipientFarmersColumns,
  actions,
  actionsForLightUser,
  actionsForOperative
} from './utils'
import InlineDialog from '~/components/InlineDialog'
import CustomIcon from '~/components/CustomIcon'
import { capitalizeFirstLetter, handleErrors } from '~/utils/helpers'
import TwoFaComp from '~/components/TwoFaComp'

const OffersPreviewMilkFixedPrice: React.FC = () => {

  //consts
  const { t } = useTranslation()
  const { id } = useParams()
  const { systemData, systemData: { amountUnit, amountVolume, amountPrice } } = useAuth()
  const priceLabel = `(${amountPrice == 'e' ? '€' : '€ Ct'}/${amountVolume > 1 ? amountVolume : ''}${amountUnit})`
  const offersPreviewColumns = dynamicOffersPreviewColumns(priceLabel, amountUnit)
  const confirmOffersColumns = dynamicConfirmOffersColumns(amountUnit)
  const participientFarmersColumns = dynamicParticipientFarmersColumns(amountUnit)
  const { systemData: { logged: { roleSlug }, operativeUsersCanSeeFarmersInfo } } = useAuth()
  const navigate = useNavigate()
  const lightUserLogged = roleSlug === LIGHT_USER_ROLE_SLUG
  const tfaConfirmOffers = systemData?.roleSettings?.tfaConfirmOffers === 1
  const tfaType = systemData?.logged?.tfaType

  // states
  const [farmerGroupTradingData, setFarmerGroupTradingData] = useState([])
  const [isBusy, setIsBusy] = useState(false)
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [openConfirmAmountModal, setOpenConfirmAmountModal] = useState(false)
  const [amount, setAmount] = useState<string | number>(0)
  const [participientFarmers, setParticipientFarmers] = useState([])
  const [openParticipientModal, setOpenParticipientModal] = useState(null)
  const [row, setRow] = useState(null)
  const [correctTfaCode, setCorrectTfaCode] = useState(false)
  const [tfaCode, setTfaCode] = useState('')
  const [openConfirmModal, setOpenConfirmModal] = useState(false)

  const confirmedColumns = JSON.parse(JSON.stringify(offersPreviewColumns.slice(0, 4)))
  confirmedColumns[3].type = 'input-confirm'
  confirmedColumns[3].name = t('Confirmed trading amount (in {{amountUnit}})', { amountUnit })

  const {
    data: offersPreviewOptions,
    isLoading: isBusySettings,
    refetch: refetchOptions
  } = MilkFixedPriceQuery.getMilkFixexPriceOffersSettings(id)

  const {
    data: offersPreviewData,
    isLoading: isBusyOptions,
    refetch: refetchPreviewData
  } = MilkFixedPriceQuery.getMilkFixexPriceOffersByOptions(id)

  async function handleParticipatedLoad(date, price) {
    setIsBusy(true)
    try {
      const response = await MilkFixedService.getOffersPreviewTradingSlot(id, { date, price })
      setParticipientFarmers(response.data)
      setOpenParticipientModal({
        date,
        price
      })
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  async function handleConfirmAmounts(data?) {
    setIsBusy(true)
    try {
      await MilkFixedService.confirmOffersPreviewTradingSlot(id, data)
      notify(t('Confirmed offers from farmers'), 'success')
      navigate(RouteTypes.MILK_FIXED_PRICE)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  async function handleSendCode() {
    setOpenConfirmModal(true)
    if (tfaType === 'email' || tfaType === 'sms') {
      setIsBusy(true)
      try {
        await ProfileSettingsService.sendCode()
        notify(t('Code sent'), 'success')
        setOpenConfirmModal(true)
      } catch (error) {
        setOpenConfirmModal(false)
        handleErrors(error)
      }
      setIsBusy(false)
    }
  }

  function renderTableFooter() {
    return <TableFooterWrapper>
      <div>
        <Label basic pointing={'left'}>
          {t('Total: ')}
          <Label.Detail>{offersPreviewData?.length || 0}</Label.Detail>
        </Label>
      </div>
      {offersPreviewOptions?.confirmAvailable && !lightUserLogged && <Button
        onClick={() => {
          if (tfaConfirmOffers) {
            handleSendCode()
            return
          }
          else {
            setOpenConfirmModal(true)
          }
        }}
        disabled={!offersPreviewOptions.confirmAvailable}
        size='small'
        primary
      >{t('Confirm trading amount')}</Button>
      }
    </TableFooterWrapper>
  }

  function handleConfirmedClicked(row) {
    setOpenConfirmAmountModal(true)
    setRow(row)
  }

  function handleActionClick(row, type) {
    const { date, price } = row
    switch (type) {
      case 'enterConfirmed':
        return handleConfirmedClicked(row)
      case 'participated':
        return handleParticipatedLoad(date, price)
      default:
        return
    }
  }

  function getActions() {
    switch (roleSlug) {
      case LIGHT_USER_ROLE_SLUG:
        return actionsForLightUser
      case OPERATIVE_ROLE_SLUG:
        return operativeUsersCanSeeFarmersInfo === 0 ? actionsForOperative : actions
      default:
        return actions
    }
  }

  function renderTable() {
    const index = activeTabIndex > 0 ? activeTabIndex - 1 : 0
    const idToShow = offersPreviewOptions.farmerGroups[index].id
    const indexPlaced = farmerGroupTradingData.findIndex(farmerGroupTrading => farmerGroupTrading.id === idToShow)
    const total = activeTabIndex > 0 ? farmerGroupTradingData[indexPlaced]?.farmers?.length || 0 : offersPreviewData?.length
    return <TableComponent
      actions={getActions()}
      tableColumnHeaders={offersPreviewColumns}
      idKey={'date'}
      handleClickAction={(row, type) => handleActionClick(row, type)}
      rows={activeTabIndex > 0 ? farmerGroupTradingData[indexPlaced]?.farmers || [] : offersPreviewData}
      customTableFooter={renderTableFooter()}
      total={total}
    />
  }

  function renderConfirmTable() {
    const index = activeTabIndex > 0 ? activeTabIndex - 1 : 0
    const idToShow = offersPreviewOptions.farmerGroups[index].id
    const indexPlaced = farmerGroupTradingData.findIndex(farmerGroupTrading => farmerGroupTrading.id === idToShow)
    const total = activeTabIndex > 0 ? farmerGroupTradingData[indexPlaced]?.farmers?.length || 0 : offersPreviewData?.length
    return <TableComponent
      tableColumnHeaders={confirmOffersColumns}
      idKey={'date'}
      rows={activeTabIndex > 0 ? farmerGroupTradingData[indexPlaced]?.farmers || [] : offersPreviewData}
      removePagination
      total={total}
    />
  }

  function renderAllFarmersGroup(confirmTable = false) {
    return <>
      {confirmTable ? renderConfirmTable() : renderTable()}
    </>
  }

  async function handleTabChange(activeTabIndex) {
    setActiveTabIndex(activeTabIndex)
    const idToShow = offersPreviewOptions.farmerGroups[activeTabIndex - 1]?.id
    const indexPlaced = farmerGroupTradingData.findIndex(farmerGroupTrading => farmerGroupTrading.id === idToShow)
    if (indexPlaced < 0) {
      setIsBusy(true)
      try {
        const idFarmer = offersPreviewOptions.farmerGroups[activeTabIndex - 1]?.id
        const response = await MilkFixedService.offersPreviewTradingSlotByMonths(id, idFarmer)
        setFarmerGroupTradingData([...farmerGroupTradingData, {
          farmers: response.data,
          id: idFarmer
        }])
      } catch (error) {
        handleErrors(error)
      }
      setIsBusy(false)
    }
  }

  function renderoffersPreviewData(confirm = false) {
    const farmerGroupTabs = offersPreviewOptions.farmerGroups.map(farmerGroup => {
      return {
        menuItem: farmerGroup.name,
        render: () => renderAllFarmersGroup(confirm)
      }
    })
    const panes = [
      {
        menuItem: t('All farmers groups'),
        render: () => renderAllFarmersGroup(confirm)
      },
      ...farmerGroupTabs
    ]

    return <Tab
      menu={{ secondary: true, pointing: true }}
      onTabChange={(e, data) => handleTabChange(data.activeIndex)}
      panes={panes}
    />
  }

  function renderConfirmModal() {

    return <Dialog
      isDialogOpen={openConfirmModal}
      textClose={t('Abort')}
      isLoading={isBusy}
      handleClose={() => {
        setTfaCode('')
        setOpenConfirmModal(false)
      }}
      style={{ width: '50%' }}
      onMount={() => setActiveTabIndex(0)}
      disableSubmit={tfaConfirmOffers ? !correctTfaCode : false}
      textSubmit={t('Confirm trading amount')}
      handleSubmit={() => handleConfirmAmounts(tfaConfirmOffers ? { code: tfaCode } : null)}
      textContent={<>
        {renderoffersPreviewData(true)}
        {tfaConfirmOffers && <TwoFaComp
          onChangeTfaCode={(code) => setTfaCode(code)}
          onChangeCorrectTfaCode={(correctTfaCode) => setCorrectTfaCode(correctTfaCode)}
        />}
      </>}
      textHeader={t('Offers confirmation')}
      type={'dialog'}
    />
  }

  async function onSubmit() {
    const idToShow = offersPreviewOptions.farmerGroups[activeTabIndex - 1]?.id
    const indexActiveFarmerGroup = farmerGroupTradingData.findIndex(farmerGroupTrading => farmerGroupTrading.id === idToShow)
    const idFarmer = farmerGroupTradingData[indexActiveFarmerGroup]?.id
    const payload = {
      ...row,
      amount: amount,
      farmerGroupId: activeTabIndex === 0 ? null : idFarmer
    }
    setIsBusy(true)
    try {
      await MilkFixedService.saveOffersPreviewTradingSlot(id, payload)
      await refetchPreviewData()
      await refetchOptions()
      notify(t('Offer saved'), 'success');
      setAmount('')
      setRow(null)
      setOpenConfirmAmountModal(false)
      // in case all groups need to refreshed
      if (activeTabIndex === 0) {
        setFarmerGroupTradingData([])
        // in case some groups need to refreshed
      } else {
        if (indexActiveFarmerGroup >= 0) {
          const response = await MilkFixedService.offersPreviewTradingSlotByMonths(id, idFarmer)
          setFarmerGroupTradingData([{
            farmers: response.data,
            id: idFarmer
          }])
        }
      }
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function renderInputConfirm(type, value) {
    switch (type) {
      case 'input-confirm':
        return <>
          <CustomInput
            value={amount}
            onChange={(value) => setAmount(value)}
            allowDecimals={false}
            suffix={` ${amountUnit}`}
            limit={parseInt(row?.offeredAmount)}
          />
        </>
      default:
        return
    }
  }

  async function deleteOffer(offerId) {
    const { date, price } = openParticipientModal
    setIsBusy(true)
    try {
      await MilkFixedService.deleteTradingOffer(id, offerId)
      const response = await MilkFixedService.getOffersPreviewTradingSlot(id, { date, price })
      setParticipientFarmers(response.data)
      refetchPreviewData()
      notify(t('Successfully deleted offer'), 'success');
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function renderDeleteOfferColumn(offerId) {
    return <InlineDialog
      control={<CustomIcon name='trash' size='large' />}
      text={t('')}
      action={() => deleteOffer(offerId)} />
  }

  function renderColumn(type, row) {
    switch (type) {
      case 'delete-offer':
        return renderDeleteOfferColumn(row?.id)
      default:
        return
    }
  }

  function renderParticipientModal() {
    const { date, price } = openParticipientModal
    const dateFormated = capitalizeFirstLetter(dayjs(date).format('MMM YYYY'))
    return <Dialog
      isDialogOpen={Boolean(openParticipientModal)}
      handleClose={() => setOpenParticipientModal(null)}
      textContent={<>
        <TableComponent
          tableColumnHeaders={offersPreviewOptions?.offerDeleteAvailable && !lightUserLogged ? [...participientFarmersColumns, {
            name: t('Actions'),
            key: 'delete',
            type: 'delete-offer'
          }] : participientFarmersColumns}
          renderColumn={(type, _, row) => renderColumn(type, row)}
          removePagination
          rows={participientFarmers}
          total={participientFarmers.length}
        />
      </>}
      style={{ width: '70%' }}
      textHeader={t('Details to {{dateFormated}} / {{price}}', { dateFormated, price })}
      type={'dialog'}
    />
  }

  function renderConfirmationAmountModal() {

    return <Dialog
      isDialogOpen={openConfirmAmountModal && Boolean(row)}
      onMount={() => setAmount(row.confirmedAmount)}
      handleClose={() => {
        setRow(null)
        setAmount('')
        setOpenConfirmAmountModal(false)
      }}
      isLoading={isBusy}
      textContent={<>
        <TableComponent
          tableColumnHeaders={confirmedColumns}
          renderColumn={(type, value) => renderInputConfirm(type, value)}
          removePagination
          rows={[row]}
          total={1}
        />
      </>}
      //@ts-ignore
      disableSubmit={parseInt(amount) > parseInt(row?.offeredAmount)}
      textSubmit={t('Confirm')}
      handleSubmit={onSubmit}
      textHeader={t('Enter confirmed amount')}
      type={'dialog'}
    />
  }

  const loading = isBusy || isBusyOptions || isBusySettings

  return <Layout
    isBusy={loading}
    segmented
    title={t('Offers Preview')}
    close={RouteTypes.MILK_FIXED_PRICE}
  >
    {offersPreviewData && offersPreviewOptions && renderoffersPreviewData()}
    {openParticipientModal && renderParticipientModal()}
    {renderConfirmationAmountModal()}
    {openConfirmModal && renderConfirmModal()}
  </Layout>

}
export default OffersPreviewMilkFixedPrice
